import { useRef, useState } from "preact/hooks";

import { useModalA11y } from "../hooks/useModalA11y";
import { useProjectShares, type UseProjectSharesResult } from "../hooks/useProjectShares";
import { useShareableUsers } from "../hooks/useShareableUsers";
import { HELP_COPY } from "../lib/helpCopy";
import { colorForPerson } from "../lib/personColor";
import {
  HELPERS_DONE_LABEL,
  helperAddedMessage,
  helperRemovedMessage,
  LET_SOMEONE_HELP_LABEL,
  leaveProjectConfirmTitle,
  leftProjectMessage,
  LEAVE_PROJECT_LABEL,
  removeHelperConfirmTitle,
  REMOVE_HELPER_LABEL,
  WHO_CAN_HELP_TITLE,
  WHOS_HELPING_HEADING,
} from "../lib/projectTasksCopy";
import { deleteProjectShare } from "../lib/api";
import type { AssigneeOption } from "./AssigneePickerSheet";
import { ConfirmDialog } from "./ConfirmDialog";
import { InfoTooltip } from "./InfoTooltip";
import { NameDot } from "./NameDot";

// Sprint-017 E7 (design §5, ADR-003 §5) — "Who's helping". The words
// collaborator / share / permission never appear; the concept is "letting
// someone help." Two surfaces:
//   - `WhosHelping` — OWNER-only roster management (add via a multi-select
//     picker, remove with a confirm). Owner + admin/api (`can_share`).
//   - `LeaveProjectControl` — a helper's quiet self-remove (Q5: no roster
//     for a helper, only the exit).

interface HelperPickerProps {
  roster: AssigneeOption[];
  existingIds: Set<string>;
  onCommit: (ids: string[]) => void;
  onCancel: () => void;
}

/** Multi-select "Who can help with this?" — reuses the `.assign-sheet`
 * chrome (design §5.1: same sheet chrome, multi-select, explicit Done). */
function HelperPicker({ roster, existingIds, onCommit, onCancel }: HelperPickerProps) {
  const titleId = "helper-picker-title";
  const firstRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const { close } = useModalA11y(containerRef, {
    active: true,
    onClose: onCancel,
    initialFocusRef: firstRef,
  });

  const candidates = roster.filter((p) => !existingIds.has(p.id));

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <>
      <div class="assign-sheet-backdrop is-open" onClick={close} />
      <div
        ref={containerRef}
        class="assign-sheet is-open"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div class="assign-sheet-handle" aria-hidden="true" />
        <h2 class="assign-sheet-title" id={titleId}>
          {WHO_CAN_HELP_TITLE}
        </h2>
        <ul class="assign-sheet-list">
          {candidates.map((option, index) => {
            const isSelected = selected.has(option.id);
            return (
              <li key={option.id}>
                <button
                  ref={index === 0 ? firstRef : undefined}
                  type="button"
                  class={`assign-option${isSelected ? " is-selected" : ""}`}
                  aria-pressed={isSelected}
                  onClick={() => toggle(option.id)}
                >
                  <span class="assign-option-main">
                    <span
                      class="name-dot"
                      aria-hidden="true"
                      style={{ background: colorForPerson(option.id) }}
                    >
                      {option.name.charAt(0)}
                    </span>
                    <span>{option.name}</span>
                  </span>
                  <span class="check-mark" aria-hidden="true">
                    ✓
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
        <button
          type="button"
          class="primary-btn"
          disabled={selected.size === 0}
          onClick={() => onCommit([...selected])}
        >
          {HELPERS_DONE_LABEL}
        </button>
        <button type="button" class="secondary-btn" onClick={close}>
          Cancel
        </button>
      </div>
    </>
  );
}

export interface WhosHelpingProps {
  projectId: string;
  projectName: string;
  roster: AssigneeOption[];
  /** Sprint-018 E4 (TD-044) — true for a plain member who owns THIS
   * project. The team-board-sourced `roster` prop is empty for a member
   * (`GET /team/board` is admin/manager-only — see `app.tsx`'s wiring
   * note), so a member-owner's add-picker instead draws its candidates
   * from `GET /me/shareable-users` (`useShareableUsers`, gated on this
   * flag). Managers/admins keep `roster` unchanged — this only swaps the
   * SHARE picker's data source, never assign-out (`can_assign_out` stays
   * the sole gate for that, untouched here). Default `false` so every
   * pre-existing caller/test keeps its current (team-board) behavior. */
  isMemberOwner?: boolean;
  onNotify: (message: string) => void;
  /** The project's helper list, when the parent already fetched it
   * (`ProjectDetailOverlay` lifts it so the checklist can name assignees
   * from the same response). Omitted, this component fetches its own. */
  shares?: UseProjectSharesResult;
}

export function WhosHelping({
  projectId,
  projectName,
  roster,
  isMemberOwner = false,
  onNotify,
  shares: lifted,
}: WhosHelpingProps) {
  const own = useProjectShares(projectId, lifted === undefined);
  const shares = lifted ?? own;
  const shareableUsers = useShareableUsers(isMemberOwner);
  const pickerRoster = isMemberOwner ? shareableUsers.users : roster;
  const [pickerOpen, setPickerOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<{ id: string; name: string } | null>(null);

  const existingIds = new Set(shares.shares.map((s) => s.user_id));

  async function commitAdd(ids: string[]) {
    setPickerOpen(false);
    for (const id of ids) {
      try {
        const created = await shares.addHelper(id);
        onNotify(
          helperAddedMessage(created.name ?? pickerRoster.find((r) => r.id === id)?.name ?? ""),
        );
      } catch {
        // A single failure shouldn't abort the rest; the list re-syncs on
        // next open.
      }
    }
  }

  async function commitRemove() {
    if (!confirmRemove) return;
    const { id, name } = confirmRemove;
    setConfirmRemove(null);
    await shares.removeHelper(id);
    onNotify(helperRemovedMessage(name, projectName));
  }

  return (
    <section class="pt-helpers-section">
      <h2 class="admin-section-heading" style={{ marginTop: "22px" }}>
        {WHOS_HELPING_HEADING}
        <InfoTooltip label={HELP_COPY.whosHelping.label} content={HELP_COPY.whosHelping.content} />
      </h2>
      {shares.shares.length > 0 ? (
        <ul class="pt-helpers admin-list">
          {shares.shares.map((share) => (
            <li key={share.user_id} class="pt-helper-row">
              <span class="assign-option-main">
                <NameDot id={share.user_id} name={share.name} />
                <span>{share.name ?? "Someone"}</span>
              </span>
              <button
                type="button"
                class="pt-helper-remove"
                onClick={() => setConfirmRemove({ id: share.user_id, name: share.name ?? "them" })}
              >
                {REMOVE_HELPER_LABEL}
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <button type="button" class="pt-add-row" onClick={() => setPickerOpen(true)}>
        {LET_SOMEONE_HELP_LABEL}
      </button>

      {pickerOpen ? (
        <HelperPicker
          roster={pickerRoster}
          existingIds={existingIds}
          onCommit={(ids) => void commitAdd(ids)}
          onCancel={() => setPickerOpen(false)}
        />
      ) : null}

      {confirmRemove ? (
        <ConfirmDialog
          open
          title={removeHelperConfirmTitle(confirmRemove.name, projectName)}
          confirmLabel={REMOVE_HELPER_LABEL}
          destructive
          onConfirm={() => void commitRemove()}
          onCancel={() => setConfirmRemove(null)}
        />
      ) : null}
    </section>
  );
}

export interface LeaveProjectControlProps {
  projectId: string;
  projectName: string;
  meId: string;
  /** Called after a successful leave — the consumer closes the overlay and
   * shows the snackbar. */
  onLeft: (message: string) => void;
}

/** A helper's quiet self-remove (design §5.2 / Q5 — no roster for a
 * helper, just the exit). */
export function LeaveProjectControl({
  projectId,
  projectName,
  meId,
  onLeft,
}: LeaveProjectControlProps) {
  const [confirm, setConfirm] = useState(false);

  async function leave() {
    setConfirm(false);
    await deleteProjectShare(projectId, meId);
    onLeft(leftProjectMessage(projectName));
  }

  return (
    <>
      <button
        type="button"
        class="secondary-btn pt-leave-link"
        style={{ marginTop: "24px" }}
        onClick={() => setConfirm(true)}
      >
        {LEAVE_PROJECT_LABEL}
      </button>
      {confirm ? (
        <ConfirmDialog
          open
          title={leaveProjectConfirmTitle(projectName)}
          body={HELP_COPY.leaveProjectConsequence}
          confirmLabel={LEAVE_PROJECT_LABEL}
          destructive
          onConfirm={() => void leave()}
          onCancel={() => setConfirm(false)}
        />
      ) : null}
    </>
  );
}
